import {Button, Card, CircularProgress, Grid, IconButton, MenuItem, Popper, TextField, TextFieldProps} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {useEffect, useRef, useState} from "react";
import {useSyncedRef} from "../../../../misc/SyncedRef";
import {red} from "@mui/material/colors";
import {distinct} from "nate-react-api-helpers";
import useWindowSize from "react-use/lib/useWindowSize";

export function AutoComplete<T>(props: Omit<TextFieldProps, "value"|"onSelect"> & {
    search: (input: string) => Promise<T[]>
    value: any;
    equals: (a: T, b: T) => boolean;
    makeLabel: (input: T) => string;
    makeLabelReact?: (input: T) => any;
    useNativeInput?: boolean;
    popperClassName?: string;
    onSelect(value: T): void;
    onMultiSelect?(value: T[]): void;
    onCreate?(value: string): void;
}) {
    const {
        search, value, equals, makeLabel, onSelect, onCreate, popperClassName,
        ...textProps
    } = props;

    const [searchStr, setSearchStr] = useState("");

    const [open, setOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<any>(null);
    const [list, setList] = useState<T[]>([]);
    const [error, setError] = useState<string|null>(null)
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if(open) return;
        setSearchStr(props.value ? makeLabel(props.value) || "" : "");
    }, [props.value, makeLabel, open])

    const searchRef = useSyncedRef(search);

    useEffect(() => {
        if(!open) return;

        let ignore = false;

        (async () => {
            try {
                setError(null);
                setLoading(true);

                const promise = await searchRef.current(searchStr);
                if (ignore) return;

                setLoading(false);
                setList(promise)
            } catch (e: any) {
                if (ignore) return;

                setLoading(false);
                setError(e.toString());
            }
        })()

        return () => {
            ignore = true;
        }
    }, [searchStr, open, searchRef]);

    const showCreate = (!list || list.filter(l => makeLabel(l) === searchStr).length === 0) && props.onCreate && searchStr;
    const [focused, setFocused] = useState(false);

    const [selected, setSelected] = useState<T[]>([]);

    useEffect(() => {
        if(selected.length > 0) return
        if(focused) {
            setOpen(focused)
            return;
        }

        // delay closing the popper to allow the user to click on the popper
        const tm = setTimeout(() => {
            setOpen(focused)
        }, 500);

        return () => clearTimeout(tm);
    }, [focused, selected]);

    const shiftKey = useRef(false)
    const ctrlKey = useRef(false)

    useEffect(() => {
        setSelected([])
    }, [list]);

    useEffect(() => {
        const keydown = (e: any) => {
            if(e.key === "Shift") {
                shiftKey.current = true;
            }

            if(e.key === "Control" || e.key === "Meta") {
                ctrlKey.current = true;
            }
        }

        const keyup = (e: any) => {
            if(e.key === "Shift") {
                shiftKey.current = false;
            }

            if(e.key === "Control" || e.key === "Meta") {
                ctrlKey.current = false;
            }
        }

        document.addEventListener("keydown", keydown)
        document.addEventListener("keyup", keyup)

        return () => {
            document.removeEventListener("keydown", keydown)
            document.removeEventListener("keyup", keyup)
        }
    }, []);

    const win = useWindowSize();

    return (
        <>
            {props.useNativeInput ? <input
                    type="text"
                    value={searchStr} onChange={e => setSearchStr(e.target.value)}
                    ref={setAnchorEl as any}
                    placeholder={props.placeholder}
                    style={Object.assign({
                        border: "none",
                        width: "100%",
                        height: "100%",
                        padding: 4,
                        paddingLeft: 6,
                        paddingRight: 6
                    }, props.style || {})}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                    className={props.className}
                    autoComplete="off"
                /> :
                <TextField
                    value={searchStr} onChange={e => setSearchStr(e.target.value)}
                    ref={setAnchorEl}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                    {...textProps}
                    autoComplete="off"
                    InputProps={{
                        ...textProps.InputProps,
                        endAdornment: (
                            <IconButton
                                size="small"
                                // don't steal focus from the input (which would blur/close it)
                                onMouseDown={e => e.preventDefault()}
                                onClick={() => {
                                    if (open) {
                                        setFocused(false);
                                        setOpen(false);
                                        return;
                                    }

                                    // open with a blank search to show all options
                                    setSearchStr("");
                                    setFocused(true);
                                    setOpen(true);
                                }}
                            >
                                <ArrowDropDownIcon />
                            </IconButton>
                        ),
                    }}
                />
            }

            <Popper open={open} style={{zIndex: 1400}}
                    anchorEl={anchorEl}
                    placement="bottom-start"
                    disablePortal={false}
                    modifiers={[
                        {
                            name: 'flip',
                            enabled: true,
                            options: {
                                altBoundary: true,
                                rootBoundary: 'viewport',
                                padding: 8,
                            },
                        },
                        {
                            name: 'preventOverflow',
                            enabled: true,
                            options: {
                                boundary: "viewport",
                                altAxis: true,
                                altBoundary: true,
                                tether: true,
                                rootBoundary: 'viewport',
                                padding: 8,
                            },
                        },
                    ]}
            >

                <Card className={props.popperClassName} style={{
                    width: anchorEl && window.getComputedStyle(anchorEl).width,
                }}>
                    {loading && <CircularProgress />}
                    <div style={{flex: 1, overflow: "auto", maxHeight: win.height * 3/4}}>
                        {!loading && error && <MenuItem style={{color: red["700"]}}>{error}</MenuItem>}
                        {list && list.map((l, index) => <MenuItem selected={selected.indexOf(l) !== -1} key={index.toString()} onClick={(e) => {
                            if (props.onMultiSelect) {
                                if (shiftKey.current) {
                                    if (selected.length > 0) {
                                        const lastItemIndex = list.indexOf(selected[selected.length - 1])
                                        const toAdd = makeRange(lastItemIndex, index).map(i => {
                                            return list[i]
                                        })

                                        setSelected(distinct([...selected, ...toAdd]));
                                    } else {
                                        setSelected([l])
                                    }

                                    return
                                }

                                if (ctrlKey.current) {
                                    // multi select
                                    setSelected(distinct([...selected, l]));
                                    return;
                                }
                            }

                            onSelect(l)
                        }}>
                            {(props.makeLabelReact || makeLabel)(l)}
                        </MenuItem>)}
                        {showCreate && <MenuItem onClick={() => {
                            console.log("create", searchStr)
                            if(onCreate) onCreate(searchStr)
                        }}>Create '{searchStr}'</MenuItem>}
                        {(!list || list.length === 0) && !showCreate && <MenuItem key="no-result">
                            <div style={{fontSize: "0.8rem", textAlign: "center"}}>No results</div>
                        </MenuItem>}
                    </div>
                    {selected.length > 0 && <div style={{padding: 8}}>
                        <Grid container spacing={1} justifyContent="flex-end">
                            <Grid item>
                                <Button size="small" color="primary" onClick={() => setSelected([])}>Cancel</Button>
                            </Grid>
                            <Grid item>
                                <Button size="small" color="primary" variant="contained" onClick={() => {
                                    if(props.onMultiSelect) props.onMultiSelect(selected)
                                    setSelected([]);
                                }}>Submit</Button>
                            </Grid>
                        </Grid>
                    </div>}
                </Card>
            </Popper>
        </>
    )
}

function makeRange(a: number, b: number) {
    if(b < a) {
        let tmp = a;
        a = b
        b = tmp;
    }

    const rs: number[] = []

    for(let i = a; i <= b; i++) {
        rs.push(i)
    }

    return rs;
}